/**
 * Страница публичного отслеживания перегона по ссылке с PIN — блок `WEB-3.2`.
 *
 * Человек без приложения и без входа открывает ссылку `/t/<токен>`, вводит PIN
 * и видит карточку работы. Одна страница, четыре состояния: ввод PIN, карточка,
 * «заперто» и «ссылка не действует» (макеты `117`–`121`).
 *
 * Клиент отображает, сервер решает (правило 4). Остаток попыток, запирание,
 * «на час», извещение заказчика и конец жизни ссылки — всё это ответ сервера:
 * страница показывает его текст дословно и не считает ничего сама. Закрывает
 * страницу только отказ `404` — статус работы в ответе ни с чем не сравнивается.
 * Давность последней точки — `age_minutes` ответа, а не часы телефона.
 *
 * PIN живёт только в памяти страницы и уходит только телом запроса: ни в
 * адрес, ни в хранилища, ни в журнал консоли. Перезагрузка снова просит PIN.
 * На запирании и на «не действует» PIN забывается, и обновление снимается.
 *
 * Показывается **закрытый перечень** полей ответа — каждое названо в разметке
 * своим местом. Поле, которого контракт не объявлял, страница не видит вовсе:
 * обхода ответа по ключам здесь нет.
 *
 * Обновление — раз в минуту и только при видимой вкладке (ответ владельца
 * `Б3`-а); следующий запрос не уходит, пока не вернулся предыдущий. Минута
 * отсчитывается от отправки запроса.
 *
 * Карта — решение `D477` (§16 спецификации): окно встроенной карты Google с
 * меткой последней точки ответа и ссылка «Открыть в картах» в Google Maps на ту
 * же точку. Линии трека нет: `track.points` на странице не рисуется. Окно живёт
 * между отрисовками и перезагружается только при смене координат. Картинки,
 * окно и ссылка не отдают адрес страницы с токеном чужому хосту: политика
 * Referer снята.
 */

import { ApiFailure } from '../api/client';
import type { ApiClient } from '../api/client';
import type { ErrorDetail, PublicTracking } from '../api/contract';
import {
  SUPPORTED_LOCALES,
  peregonVehicleKindStringKey,
  resolveLocale,
  setLocale,
  workStatusStringKey,
} from '../i18n';
import type { Locale, Strings } from '../i18n';
import { createFieldMessageSlot, enumLabel, momentTimeLabel, renderFieldMessages, trackingRefusalFor } from './state';

export interface PublicTrackingOptions {
  readonly client: ApiClient;
  /** Токен ссылки — из пути документа, разобранного маршрутизатором. */
  readonly token: string;
  /** Начальное значение языка. Без него — разрешение из браузера. */
  readonly locale?: Locale;
}

/** Минут в часе и в сутках — единицы давности, а не пороги правила. */
const MINUTES_IN_HOUR = 60;
const MINUTES_IN_DAY = 24 * MINUTES_IN_HOUR;

/** Шаг обновления — ответ владельца `Б3`-а: раз в минуту. */
const REFRESH_EVERY_MS = 60_000;

/**
 * Карты Google — единственный чужой адрес страницы (решение `D477`, исключение
 * сторожа `F1` по месту): от него строятся и окно карты, и ссылка.
 */
const GOOGLE_MAPS = 'https://www.google.com/maps';

/** Масштаб окна карты — город и подъезды к нему (§16). */
const MAP_ZOOM = 13;

/** Политика Referer картинок, окна карты и ссылки: адрес страницы несёт токен. */
const NO_REFERRER = 'no-referrer';

type Phase = 'pin' | 'card' | 'locked' | 'gone';

/** Миниатюра кадра: чей кадр, по какой ссылке показан и не сорвалась ли загрузка. */
interface Thumb {
  readonly createdAt: string;
  readonly url: string;
  failed: boolean;
}

/** Ссылки миниатюр после ответа: тот же кадр на том же месте держит свою. */
function keepThumbs(previous: readonly Thumb[], answer: PublicTracking['handover_photos']): Thumb[] {
  const next: Thumb[] = [];

  answer.forEach((photo, index) => {
    const shown = previous[index];

    if (shown !== undefined && !shown.failed && shown.createdAt === photo.created_at) {
      next.push(shown);
    } else {
      next.push({ createdAt: photo.created_at, url: photo.url, failed: false });
    }
  });

  return next;
}

/** Метка элемента для тестов и для ручного бланка — словарь §11 спецификации. */
function mark<T extends Element>(node: T, name: string): T {
  node.setAttribute('data-tracking', name);

  return node;
}

/** Адрес страницы с токеном не уходит ни хосту фото, ни приложению карт. */
function dropReferrer(node: Element): void {
  node.setAttribute('referrerpolicy', NO_REFERRER);
}

/**
 * Дети узла становятся ровно этим списком, а ребёнок, уже стоящий в узле, с
 * дерева не снимается: окно карты, вынутое из дерева хоть на миг, браузер
 * грузит заново. Сначала уходят лишние, затем недостающие встают на свои
 * места перед оставшимися — оставшиеся не двигаются.
 */
function settle(parent: Element, children: readonly Node[]): void {
  const wanted = new Set(children);

  for (const child of [...parent.childNodes]) {
    if (!wanted.has(child)) {
      child.remove();
    }
  }

  children.forEach((child, index) => {
    const standing = parent.childNodes[index] ?? null;

    if (standing !== child) {
      parent.insertBefore(child, standing);
    }
  });
}

/** Название страны средой по коду ISO; без среды или на чужом коде — код. */
function countryName(locale: Locale, code: string): string {
  if (typeof Intl.DisplayNames !== 'function') {
    return code;
  }

  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/**
 * Давность словами — из минут ответа сервера, часы телефона не участвуют.
 *
 * Единица — по величине, как у Android (`positionAgeCaption`, решение `D135`,
 * §15 `Н14`): меньше часа — минуты, меньше суток — часы, иначе сутки; деление
 * с отбрасыванием остатка. Это форматирование, а не правило сервера. Ноль —
 * «сейчас» словами среды (`numeric: 'auto'`); прочее — с числом: «1 день
 * назад», а не «вчера» — сутки назад не обязательно вчера.
 */
function ageLabel(locale: Locale, minutes: number): string {
  if (typeof Intl.RelativeTimeFormat !== 'function') {
    return String(minutes);
  }

  let value = minutes;
  let unit: Intl.RelativeTimeFormatUnit = 'minute';

  if (minutes >= MINUTES_IN_DAY) {
    value = Math.floor(minutes / MINUTES_IN_DAY);
    unit = 'day';
  } else if (minutes >= MINUTES_IN_HOUR) {
    value = Math.floor(minutes / MINUTES_IN_HOUR);
    unit = 'hour';
  }

  const numeric = value === 0 ? 'auto' : 'always';

  return new Intl.RelativeTimeFormat(locale, { numeric }).format(-value, unit);
}

/** Расстояние единицей среды; среда без единиц — число как есть. */
function distanceLabel(locale: Locale, kilometres: number): string {
  try {
    return new Intl.NumberFormat(locale, { style: 'unit', unit: 'kilometer' }).format(kilometres);
  } catch {
    return String(kilometres);
  }
}

export function mountPublicTracking(root: HTMLElement, options: PublicTrackingOptions): HTMLElement {
  const { client, token } = options;
  const doc = root.ownerDocument;
  const page = doc.createElement('div');

  page.className = 'tracking';
  root.replaceChildren(page);

  let locale: Locale = options.locale ?? resolveLocale(navigator.languages);
  let strings: Strings = setLocale(locale);

  let phase: Phase = 'pin';
  /** PIN, принятый сервером, — для обновления. Только память страницы. */
  let pin: string | null = null;
  /** Набранное в поле — переживает перерисовку при смене языка. */
  let draft = '';
  let pinDetails: readonly ErrorDetail[] = [];
  let pinReason: string | null = null;
  let pinOffline = false;
  let data: PublicTracking | null = null;
  let cardOffline = false;
  /**
   * Отказ вне перечня на обновлении (§15 `Н10`): сервер ответил своим
   * конвертом — у карточки его текст, а не «нет связи». Снимается удачным
   * ответом.
   */
  let cardServerText: string | null = null;
  /** Текст сервера на экранах «заперто» и «не действует». */
  let serverText = '';
  /** Номер кадра, открытого в полный размер; ссылка берётся последнего ответа. */
  let viewer: number | null = null;
  /**
   * Ссылки миниатюр по местам ответа (§15 `Н9`). Сервер подписывает ссылку
   * кадра заново на каждый ответ; показанная миниатюра её не меняет, и
   * минутное обновление не перекачивает фото приёмки. Кадр опознаётся по
   * `created_at` и месту; не загрузившаяся миниатюра берёт ссылку следующего
   * ответа.
   */
  let thumbs: Thumb[] = [];
  /**
   * Окно карты и его предки на карточке живут между отрисовками (§16): перенос
   * окна в дереве и запись его адреса браузер исполняет как новую загрузку.
   * Карточка и блок положения поэтому не пересоздаются, а перестилаются, и
   * адрес окна пишется только при смене координат.
   */
  let cardNode: HTMLElement | null = null;
  let positionNode: HTMLElement | null = null;
  let mapNode: HTMLIFrameElement | null = null;
  let mapAddress: string | null = null;

  let inFlight = false;
  /** Смена языка во время запроса: повторить, когда он вернётся. */
  let repeat = false;
  let lastRequestAt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;

  function applyLocale(next: Locale): void {
    locale = next;
    strings = setLocale(next);
    doc.documentElement.lang = next;
    client.useLocale(next);
  }

  function clearTimer(): void {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  /** Страница снята с документа — обновлять некого: всё останавливается. */
  function alive(): boolean {
    if (page.isConnected) {
      return true;
    }

    clearTimer();
    pin = null;
    doc.removeEventListener('visibilitychange', onVisibility);

    return false;
  }

  /** Карточки на странице нет — следующая строится с нового окна карты. */
  function forgetCard(): void {
    cardNode = null;
    positionNode = null;
    mapNode = null;
    mapAddress = null;
  }

  /** Запирание и «не действует»: обновления больше нет, PIN забыт. */
  function stop(): void {
    clearTimer();
    pin = null;
    draft = '';
    repeat = false;
    data = null;
    viewer = null;
    thumbs = [];
    cardOffline = false;
    cardServerText = null;
    forgetCard();
  }

  function schedule(): void {
    clearTimer();

    if (phase !== 'card' || pin === null || inFlight || doc.visibilityState !== 'visible') {
      return;
    }

    const wait = lastRequestAt + REFRESH_EVERY_MS - Date.now();

    if (wait <= 0) {
      void ask(pin);

      return;
    }

    timer = setTimeout(() => {
      timer = null;
      schedule();
    }, wait);
  }

  function onVisibility(): void {
    if (!alive()) {
      return;
    }

    // Скрытая вкладка не спрашивает; вернувшаяся спрашивает сразу, если с
    // прошлого запроса прошла минута, а иначе — когда она пройдёт.
    if (doc.visibilityState === 'visible') {
      schedule();
    } else {
      clearTimer();
    }
  }

  function settleFailure(error: unknown): void {
    const failure = error instanceof ApiFailure ? error : null;
    const refusal = trackingRefusalFor(failure?.code ?? null);
    const reason = failure?.reason ?? null;

    if (refusal === 'gone') {
      stop();
      phase = 'gone';
      serverText = reason ?? '';

      return;
    }

    if (refusal === 'locked') {
      // Ссылку может запереть и чужой посетитель тремя неверными PIN — экран
      // тот же и на обновлении карточки.
      stop();
      phase = 'locked';
      serverText = reason ?? '';

      return;
    }

    if (refusal === 'pin') {
      // PIN сменился у заказчика либо введён неверно: карточка снимается, поле
      // пустое, у поля — текст сервера. Попыток страница не считает.
      stop();
      phase = 'pin';
      pinDetails = failure?.details ?? [];
      pinReason = reason;
      pinOffline = false;

      return;
    }

    if (phase === 'card') {
      // Отказ на обновлении: карточка остаётся, обновление идёт. Конверт
      // сервера — его текст (§15 `Н10`); без конверта — «нет связи».
      cardOffline = reason === null;
      cardServerText = reason;

      return;
    }

    pinDetails = failure?.details ?? [];
    pinReason = reason;
    pinOffline = refusal === 'offline';
  }

  async function ask(candidate: string): Promise<void> {
    if (inFlight || !alive()) {
      return;
    }

    inFlight = true;
    lastRequestAt = Date.now();
    clearTimer();

    try {
      const answer = await client.viewPublicTracking(token, candidate);

      inFlight = false;

      if (!alive()) {
        return;
      }

      phase = 'card';
      pin = candidate;
      data = answer;
      thumbs = keepThumbs(thumbs, answer.handover_photos);
      draft = '';
      cardOffline = false;
      cardServerText = null;
      pinDetails = [];
      pinReason = null;
      pinOffline = false;

      if (viewer !== null && viewer >= answer.handover_photos.length) {
        viewer = null;
      }
    } catch (error) {
      inFlight = false;

      if (!alive()) {
        return;
      }

      settleFailure(error);
    }

    render();

    if (repeat && phase === 'card' && pin !== null) {
      repeat = false;
      void ask(pin);

      return;
    }

    repeat = false;
    schedule();
  }

  function selectLocale(next: Locale): void {
    applyLocale(next);
    render();

    // Города приходят с сервера на языке запроса: на карточке смена языка —
    // повтор с тем же PIN. На вводе PIN спрашивать нечего.
    if (inFlight) {
      repeat = true;
    } else if (phase === 'card' && pin !== null) {
      void ask(pin);
    }
  }

  function caption(text: string): HTMLElement {
    const node = doc.createElement('p');

    node.className = 'tracking__caption';
    node.textContent = text;

    return node;
  }

  function moment(at: string): HTMLElement {
    const node = doc.createElement('time');

    node.setAttribute('datetime', at);
    node.textContent = momentTimeLabel(locale, at);

    return node;
  }

  function header(): HTMLElement {
    const node = doc.createElement('header');
    const title = doc.createElement('h1');
    const switcher = doc.createElement('nav');
    const label = doc.createElement('span');

    node.className = 'tracking__header';
    title.className = 'tracking__title';
    title.textContent = strings.tracking_title;
    switcher.className = 'switcher switcher--locale';
    label.className = 'switcher__caption';
    label.textContent = strings.language_label;
    switcher.append(label);

    for (const option of SUPPORTED_LOCALES) {
      const item = mark(doc.createElement('button'), 'locale-option');

      item.type = 'button';
      item.className = 'switcher__item';
      // Подпись — код языка, как в шапке кабинета: названий языков в наборах нет.
      item.textContent = option;
      item.setAttribute('data-locale', option);
      item.setAttribute('aria-current', String(option === locale));
      item.addEventListener('click', () => {
        selectLocale(option);
      });
      switcher.append(item);
    }

    node.append(title, switcher);

    return node;
  }

  function pinForm(): HTMLElement {
    const form = mark(doc.createElement('form'), 'pin-form');
    const hint = doc.createElement('p');
    const field = doc.createElement('label');
    const fieldCaption = doc.createElement('span');
    const input = mark(doc.createElement('input'), 'pin');
    const message = mark(createFieldMessageSlot(), 'pin-error');
    const submit = mark(doc.createElement('button'), 'pin-submit');

    form.className = 'tracking__form';
    form.noValidate = true;
    hint.className = 'tracking__hint';
    hint.textContent = strings.tracking_pin_hint;
    field.className = 'form-field';
    fieldCaption.className = 'form-field__caption';
    fieldCaption.textContent = strings.tracking_pin_label;

    // Формы PIN клиент не проверяет: «шесть цифр» — правило сервера, и его
    // отказ `422` встанет у поля своим текстом.
    input.name = 'pin';
    input.className = 'tracking__pin';
    input.setAttribute('inputmode', 'numeric');
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('maxlength', '6');
    input.value = draft;
    input.addEventListener('input', () => {
      draft = input.value;
    });

    submit.type = 'submit';
    submit.className = 'action action--primary';
    submit.textContent = strings.tracking_pin_submit;

    field.append(fieldCaption, input, message);
    form.append(hint, field);

    if (pinOffline) {
      const offline = mark(doc.createElement('p'), 'offline');

      offline.className = 'notice';
      offline.setAttribute('role', 'alert');
      offline.textContent = strings.tracking_offline;
      form.append(offline);
    }

    form.append(submit);
    form.addEventListener('submit', (event) => {
      // Отправка не уходит по адресу формы: PIN — только телом обращения.
      event.preventDefault();
      draft = input.value;
      void ask(input.value);
    });

    renderFieldMessages(form, pinDetails);

    if (message.textContent === '' && pinReason !== null) {
      // Отказ без разбора по полю — текст сервера целиком, туда же.
      message.textContent = pinReason;
    }

    return form;
  }

  function notice(name: string, title: string, retry: boolean): HTMLElement {
    const section = mark(doc.createElement('section'), name);
    const heading = doc.createElement('h2');
    const text = mark(doc.createElement('p'), 'server-text');

    section.className = 'tracking__notice';
    heading.textContent = title;
    text.textContent = serverText;
    section.append(heading, text);

    if (retry) {
      const again = mark(doc.createElement('button'), 'retry');

      again.type = 'button';
      again.className = 'action';
      again.textContent = strings.tracking_retry;
      again.addEventListener('click', () => {
        phase = 'pin';
        draft = '';
        pinDetails = [];
        pinReason = null;
        pinOffline = false;
        serverText = '';
        render();
      });
      section.append(again);
    }

    return section;
  }

  function block(name: string, ...children: Node[]): HTMLElement {
    const node = mark(doc.createElement('div'), name);

    node.className = 'tracking__block';
    node.append(...children);

    return node;
  }

  function text(value: string, className: string): HTMLElement {
    const node = doc.createElement('span');

    node.className = className;
    node.textContent = value;

    return node;
  }

  /**
   * Окно карты последней точки (§16): одно на карточку и живёт между
   * отрисовками. Адрес пишется только при смене координат — та же точка окно
   * не перезагружает; заголовок меняется вместе с языком, это загрузки не
   * вызывает.
   */
  function mapWindow(last: { readonly lat: number; readonly lng: number }): HTMLIFrameElement {
    const address = `${GOOGLE_MAPS}?q=${last.lat},${last.lng}&z=${MAP_ZOOM}&output=embed`;
    let frame = mapNode;

    if (frame === null) {
      frame = mark(doc.createElement('iframe'), 'track');
      frame.className = 'tracking__frame';
      // Политика и лень — до адреса: первая же загрузка идёт без Referer.
      dropReferrer(frame);
      frame.setAttribute('loading', 'lazy');
      mapNode = frame;
    }

    if (mapAddress !== address) {
      frame.src = address;
      mapAddress = address;
    }

    frame.title = strings.tracking_map_title;

    return frame;
  }

  function position(shown: PublicTracking): HTMLElement {
    const last = shown.last_position;
    const node = positionNode ?? mark(doc.createElement('div'), 'position');

    positionNode = node;
    node.className = 'tracking__block';

    if (last === null) {
      const none = mark(doc.createElement('p'), 'no-position');

      none.className = 'tracking__muted';
      none.textContent = strings.tracking_no_position;
      mapNode = null;
      mapAddress = null;
      settle(node, [caption(strings.tracking_last_position), none]);

      return node;
    }

    const age = mark(text(ageLabel(locale, last.age_minutes), 'tracking__age'), 'age');
    const coordinates = mark(text(`${last.lat}, ${last.lng}`, 'tracking__coordinates'), 'last-position');
    const map = mark(doc.createElement('a'), 'open-map');

    map.className = 'tracking__map';
    map.href = `${GOOGLE_MAPS}/search/?api=1&query=${last.lat},${last.lng}`;
    map.target = '_blank';
    map.rel = 'noreferrer noopener';
    map.textContent = strings.tracking_open_map;
    dropReferrer(map);

    settle(node, [caption(strings.tracking_last_position), age, coordinates, mapWindow(last), map]);

    return node;
  }

  function photos(shown: PublicTracking): HTMLElement {
    const grid = doc.createElement('div');

    grid.className = 'tracking__photos';

    shown.handover_photos.forEach((photo, index) => {
      const button = mark(doc.createElement('button'), 'photo');
      const image = doc.createElement('img');
      const thumb = thumbs[index];

      button.type = 'button';
      button.className = 'tracking__photo';
      button.setAttribute('aria-label', strings.tracking_photo_open);
      image.src = thumb?.url ?? photo.url;
      image.alt = '';
      dropReferrer(image);
      image.addEventListener('error', () => {
        // Не загрузилась — следующий ответ даст ей свежую ссылку.
        if (thumb !== undefined) {
          thumb.failed = true;
        }
      });
      button.append(image);

      if (photo.plate) {
        button.append(text(strings.tracking_photo_plate, 'tracking__plate'));
      }

      button.addEventListener('click', () => {
        viewer = index;
        render();
      });
      grid.append(button);
    });

    return block('photos', caption(strings.tracking_photos), grid);
  }

  function photoViewer(shown: PublicTracking): HTMLElement | null {
    const photo = viewer === null ? undefined : shown.handover_photos[viewer];

    if (photo === undefined) {
      return null;
    }

    const overlay = mark(doc.createElement('div'), 'photo-viewer');
    const image = doc.createElement('img');
    const close = mark(doc.createElement('button'), 'photo-close');

    overlay.className = 'tracking__viewer';
    overlay.setAttribute('role', 'dialog');
    image.src = photo.url;
    image.alt = strings.tracking_photos;
    dropReferrer(image);
    close.type = 'button';
    close.className = 'action';
    close.textContent = strings.tracking_photo_close;
    close.addEventListener('click', () => {
      viewer = null;
      render();
    });
    overlay.append(image, close);

    return overlay;
  }

  function card(shown: PublicTracking): HTMLElement {
    const section = cardNode ?? mark(doc.createElement('section'), 'card');
    const parts: Node[] = [];

    cardNode = section;
    section.className = 'tracking__card';

    if (cardOffline) {
      const offline = mark(doc.createElement('p'), 'offline');

      offline.className = 'notice';
      offline.setAttribute('role', 'status');
      offline.textContent = strings.tracking_offline_refresh;
      parts.push(offline);
    }

    if (cardServerText !== null) {
      const refused = mark(doc.createElement('p'), 'server-text');

      refused.className = 'notice';
      refused.setAttribute('role', 'status');
      refused.textContent = cardServerText;
      parts.push(refused);
    }

    parts.push(
      block(
        'status',
        text(enumLabel(strings, workStatusStringKey(shown.status)), 'tracking__status'),
        caption(strings.tracking_status_changed_at),
        moment(shown.status_changed_at),
      ),
    );

    const route: Node[] = [caption(strings.tracking_route)];
    const ends: string[] = [];

    for (const end of [shown.route.origin, shown.route.destination]) {
      if (end !== null) {
        ends.push(end);
      }
    }

    route.push(text(ends.join(' → '), 'tracking__value'));

    if (shown.route.distance_km !== null) {
      route.push(caption(strings.tracking_distance));
      route.push(text(distanceLabel(locale, shown.route.distance_km), 'tracking__value'));
    }

    parts.push(block('route', ...route));

    const countries: string[] = [];

    for (const code of shown.route.transit_countries) {
      countries.push(countryName(locale, code));
    }

    const transit: Node[] = [caption(strings.tracking_transit), text(countries.join(', '), 'tracking__value')];

    if (shown.route.border_crossings !== null) {
      transit.push(caption(strings.tracking_border_crossings));
      transit.push(text(String(shown.route.border_crossings), 'tracking__value'));
    }

    parts.push(block('transit', ...transit));

    const vehicle: string[] = [];

    if (shown.vehicle.kind !== null) {
      vehicle.push(enumLabel(strings, peregonVehicleKindStringKey(shown.vehicle.kind)));
    }

    if (shown.vehicle.model !== null) {
      vehicle.push(shown.vehicle.model);
    }

    parts.push(block('vehicle', caption(strings.tracking_vehicle), text(vehicle.join(' · '), 'tracking__value')));
    parts.push(position(shown));

    if (shown.handover_photos.length > 0) {
      parts.push(photos(shown));
    }

    const milestones = mark(doc.createElement('ol'), 'milestones');

    milestones.className = 'tracking__milestones';

    // Порядок — ответа: вехи не пересортировываются и не отбираются.
    for (const milestone of shown.milestones) {
      const row = doc.createElement('li');

      row.append(text(enumLabel(strings, workStatusStringKey(milestone.status)), 'tracking__value'), moment(milestone.at));
      milestones.append(row);
    }

    parts.push(caption(strings.tracking_milestones), milestones);

    const opened = photoViewer(shown);

    if (opened !== null) {
      parts.push(opened);
    }

    settle(section, parts);

    return section;
  }

  function render(): void {
    let body: HTMLElement;

    switch (phase) {
      case 'pin':
        body = pinForm();
        break;
      case 'card':
        body = data === null ? pinForm() : card(data);
        break;
      case 'locked':
        body = notice('locked', strings.tracking_locked_title, true);
        break;
      case 'gone':
        body = notice('gone', strings.tracking_gone_title, false);
        break;
    }

    if (body !== cardNode) {
      forgetCard();
    }

    // Перестилка, а не замена: карточка с окном карты остаётся на месте.
    settle(page, [header(), body]);
  }

  applyLocale(locale);
  doc.addEventListener('visibilitychange', onVisibility);
  render();

  return page;
}
